import Cancel from '@mui/icons-material/Cancel';
import CheckCircle from '@mui/icons-material/CheckCircle';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Accordion, AccordionDetails, AccordionSummary, Box, Chip, Paper, Stack, Typography } from '@mui/material';
import Grid from '@mui/material/Grid';
import { BikeleasingContractDetailsDTO, BikeleasingContractDetailsDTOInsuranceCoverageEnum } from 'probonio-shared-ui/api';
import { isCompanyOrDealerBike } from 'probonio-shared-ui/utils/bikeleasingSpecialBike';
import { getBikeleasingContractStatusColor } from 'probonio-shared-ui/utils/getBikeleasingContractStatusColor';
import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useContractButtonsForStatus } from './useContractButtonsForStatus';

interface Props {
  contract: BikeleasingContractDetailsDTO;
}

const LabelValueItem: React.FC<{
  label: string;
  value?: string;
  dataTestId?: string;
}> = ({ label, value, dataTestId }) => {
  return (
    <Box display="flex" flexDirection="row" alignItems="center" my={1}>
      <Typography variant="body2" color="text.secondary" fontWeight="bold">
        {label}
      </Typography>
      <Typography variant="body2" fontWeight="bold" flexGrow={1} textAlign="right" data-test-id={dataTestId}>
        {value}
      </Typography>
    </Box>
  );
};

const CheckOrXIcon: React.FC<{ check: boolean }> = ({ check }) => {
  return check ? <CheckCircle color="success" /> : <Cancel color="error" />;
};

export const BikeleasingContractDetails: React.FC<Props> = ({ contract }) => {
  const { t } = useTranslation('benefitsModule');
  const isSpecialBike = isCompanyOrDealerBike(contract.employee);
  const contractButtons = useContractButtonsForStatus(contract.id, contract.status, isSpecialBike);

  const renderEuro = useCallback(
    (value: number | undefined): string => (value !== undefined ? t('common:money', { money: value }) : ''),
    [t],
  );
  const renderDate = useCallback((value: string | undefined): string => (value ? t('common:date', { date: new Date(value) }) : ''), [t]);

  return (
    <Box>
      <Grid container direction="row" spacing={3} mb={2}>
        <Grid size={{ xs: 8 }} display="flex" gap={0.5}>
          <Typography variant="body2" gutterBottom>
            {t('extensions:bikeleasing_v2_benefit.contractIntro')}{' '}
            {!isSpecialBike && t('extensions:bikeleasing_v2_benefit.contractAppInfo')}
          </Typography>
        </Grid>
        <Grid size={{ xs: 4 }} direction="row" display="flex" justifyContent="flex-end" alignItems="center" gap={1}>
          <Chip
            label={t(`extensions:bikeleasing_v2_benefit.contract.status.${contract.status ?? 'UNKNOWN'}`)}
            variant="filled"
            color={getBikeleasingContractStatusColor(contract.status)}
            sx={{
              borderRadius: 1,
              height: theme => theme.spacing(3),
              fontSize: theme => theme.typography.h3.fontSize,
              fontWeight: '600',
              '& .MuiChip-label': {
                px: 1.5,
              },
            }}
          />
        </Grid>
      </Grid>
      <Grid container spacing={3} mb={3}>
        <Grid size={{ xs: 12, md: 8 }}>
          <Stack direction="column" gap={3}>
            <Paper>
              <Accordion defaultExpanded>
                <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="contract-data-content" id="contract-data-header">
                  <Typography component="span" variant="h3">
                    {t('extensions:bikeleasing_v2_benefit.section.contract')}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <LabelValueItem
                    label={t('extensions:bikeleasing_v2_benefit.contract.contractNumber')}
                    value={contract.contractNumber}
                    dataTestId="bike-field-contractNumber"
                  />
                  <LabelValueItem
                    label={t('extensions:bikeleasing_v2_benefit.contract.startDate')}
                    value={renderDate(contract.startDate)}
                  />
                  <LabelValueItem label={t('extensions:bikeleasing_v2_benefit.contract.endDate')} value={renderDate(contract.endDate)} />
                  <LabelValueItem label={t('extensions:bikeleasing_v2.contract.employeeUserId')} value={contract.employee.contactCode} />
                  <LabelValueItem
                    label={t('extensions:bikeleasing_v2.contract.monetaryAdvantage')}
                    value={renderEuro(contract.benefitInKind?.bike?.amount)}
                  />
                  <LabelValueItem
                    label={t('extensions:bikeleasing_v2.contract.monthlyLeasingRate')}
                    value={renderEuro(contract.leasingRate?.effectiveLeasingRate?.grossAmount)}
                  />
                  <Box display="flex" flexDirection="row" alignItems="flex-start">
                    <Typography variant="body2" color="text.secondary" fontWeight="bold">
                      {t('extensions:bikeleasing_v2.contract.insuranceCoverage.label')}
                    </Typography>
                    {/* Show this check always, as this is the default for all employers*/}
                    <Box display="flex" flexDirection="column" alignItems="flex-end" flexGrow={1}>
                      <Box display="flex" flexDirection="row" alignItems="center" gap={1} mb={1}>
                        <Typography variant="body2" fontWeight="bold" flexGrow={1} textAlign="right">
                          {t('extensions:bikeleasing_v2.insurance.comfort')}
                        </Typography>
                        <CheckOrXIcon check={true} />
                      </Box>
                      <Box display="flex" flexDirection="row" alignItems="center" gap={1} mb={1}>
                        <Typography variant="body2" fontWeight="bold" flexGrow={1} textAlign="right">
                          {t('extensions:bikeleasing_v2.insurance.wearAndTear')}
                        </Typography>
                        <CheckOrXIcon
                          check={contract.insuranceCoverage.includes(BikeleasingContractDetailsDTOInsuranceCoverageEnum.PremiumPlus)}
                        />
                      </Box>
                      <Box display="flex" flexDirection="row" alignItems="center" gap={1}>
                        <Typography variant="body2" fontWeight="bold" flexGrow={1} textAlign="right">
                          {t('extensions:bikeleasing_v2.insurance.inspectionPackage')}
                        </Typography>
                        <CheckOrXIcon
                          check={contract.insuranceCoverage.includes(BikeleasingContractDetailsDTOInsuranceCoverageEnum.InspectionPlus)}
                        />
                      </Box>
                    </Box>
                  </Box>
                </AccordionDetails>
              </Accordion>
            </Paper>

            <Paper>
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="contract-bike-content" id="contract-bike-header">
                  <Typography component="span" variant="h3">
                    {t('extensions:bikeleasing_v2_benefit.section.bike')}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <LabelValueItem label={t('extensions:bikeleasing_v2_benefit.contract.brand')} value={contract.bike.brand} />
                  <LabelValueItem label={t('extensions:bikeleasing_v2_benefit.contract.model')} value={contract.bike.model} />
                  <LabelValueItem label={t('extensions:bikeleasing_v2.contract.bike.frameNumber')} value={contract.bike.frameNo} />
                  <LabelValueItem label={t('extensions:bikeleasing_v2.contract.bike.frameHeight')} value={contract.bike.frameSize} />
                  <LabelValueItem label={t('extensions:bikeleasing_v2.contract.bike.bikeType')} value={contract.bike.bikeType} />
                  <LabelValueItem label={t('extensions:bikeleasing_v2.contract.bike.basicColor')} value={contract.bike.color} />
                  <LabelValueItem
                    label={t('extensions:bikeleasing_v2.contract.accessories.optionalAccessoriesRequested')}
                    value={contract.bike.accessoryWish || contract.accessories.description}
                  />
                  <LabelValueItem
                    label={t('extensions:bikeleasing_v2.contract.accessories.lockBrand')}
                    value={contract.accessories.lockBrand}
                  />
                  <LabelValueItem label={t('extensions:bikeleasing_v2.contract.accessories.typeOfLock')} value={contract.bike.typeOfLock} />
                  <LabelValueItem
                    label={t('extensions:bikeleasing_v2.contract.recommendedRetailPriceNet')}
                    value={renderEuro(contract.leasingData.purchasePriceNet?.amount)}
                  />
                  <LabelValueItem
                    label={t('extensions:bikeleasing_v2.contract.recommendedRetailPriceGross')}
                    value={renderEuro(contract.leasingData.rrpBike?.amount)}
                  />
                </AccordionDetails>
              </Accordion>
            </Paper>

            <Paper>
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="contract-bike-content" id="contract-bike-header">
                  <Typography component="span" variant="h3">
                    {t('extensions:bikeleasing_v2.contract.logisticData')}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <LabelValueItem label={t('extensions:bikeleasing_v2.contract.deliveryDate')} value={renderDate(contract.transferDate)} />
                  <Box display="flex" flexDirection="row" alignItems="flex-start">
                    <Typography variant="body2" color="text.secondary" fontWeight="bold">
                      {t('extensions:bikeleasing_v2.contract.retailer')}
                    </Typography>
                    <Box display="flex" flexDirection="column" alignItems="flex-end" flexGrow={1}>
                      <Typography variant="body2" fontWeight="bold" flexGrow={1} textAlign="right">
                        {contract.retailer?.name}
                      </Typography>
                      <Typography variant="body2" fontWeight="bold" flexGrow={1} textAlign="right">
                        {contract.retailer?.street}
                      </Typography>
                      <Typography variant="body2" fontWeight="bold" flexGrow={1} textAlign="right">
                        {contract.retailer?.zip} {contract.retailer?.city}
                      </Typography>
                      <Typography variant="body2" fontWeight="bold" flexGrow={1} textAlign="right">
                        {contract.retailer?.phone}
                      </Typography>
                    </Box>
                  </Box>
                </AccordionDetails>
              </Accordion>
            </Paper>
          </Stack>
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <Stack direction="column" gap={1}>
            {contractButtons}
          </Stack>
        </Grid>
      </Grid>
    </Box>
  );
};
